import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { COMPANY_INFO, NAV_ITEMS } from "../data/siteData";

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-50 w-full transition-all duration-300">
      {/* Main Navigation Bar (White Background) */}
      <div className={`bg-white transition-all ${scrolled ? "py-2.5" : "py-3.5"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group focus:outline-none rounded p-1">
            <img
              src={COMPANY_INFO.logoUrl}
              alt={COMPANY_INFO.name}
              className="h-16 sm:h-20 lg:h-24 w-auto object-contain transition-transform group-hover:scale-102"
            />
          </Link>

          {/* Desktop Navigation Menu */}
          <nav className="hidden min-[1100px]:flex items-center gap-6">
            {NAV_ITEMS.map((item) => {
              if (item.children) {
                const isChildActive = item.children.some((child) => isActive(child.path));
                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => setServicesDropdownOpen(true)}
                    onMouseLeave={() => setServicesDropdownOpen(false)}
                  >
                    <button
                      className={`py-1 text-xs sm:text-sm font-bold uppercase tracking-wider transition-all flex items-center gap-1 focus:outline-none ${
                        isChildActive
                          ? "text-[#E07A38] border-b-2 border-[#E07A38]"
                          : "text-stone-900 hover:text-[#E07A38]"
                      }`}
                      onClick={() => setServicesDropdownOpen(!servicesDropdownOpen)}
                    >
                      <span>{item.label}</span>
                      <i className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${servicesDropdownOpen ? "rotate-180 text-[#E07A38]" : "text-stone-500"}`} aria-hidden="true" />
                    </button>

                    {/* Dropdown Menu */}
                    {servicesDropdownOpen && (
                      <div className="absolute left-0 top-full pt-2 w-72 z-50 animate-fadeIn">
                        <div className="bg-white border border-stone-200 rounded-lg shadow-2xl overflow-hidden p-2 space-y-1">
                          {item.children.map((child) => (
                            <Link
                              key={child.path}
                              to={child.path}
                              className={`flex items-center gap-2 px-3.5 py-2.5 text-xs font-bold uppercase tracking-wider rounded transition-all ${
                                isActive(child.path)
                                  ? "bg-[#E07A38] text-white"
                                  : "text-stone-900 hover:bg-stone-100 hover:text-[#E07A38]"
                              }`}
                            >
                              <span>{child.label}</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`py-1 text-xs sm:text-sm font-bold uppercase tracking-wider transition-all relative ${
                    isActive(item.path)
                      ? "text-[#E07A38] border-b-2 border-[#E07A38]"
                      : "text-stone-900 hover:text-[#E07A38]"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Right Action & Social Icons Bar */}
          <div className="flex items-center gap-3">
            {/* Instagram Circular Icon Button */}
            <a
              href={COMPANY_INFO.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Følg Kærby Tømrer på Instagram"
              className="hidden sm:flex w-8 h-8 rounded-full bg-black text-white items-center justify-center hover:bg-[#E07A38] transition-colors"
            >
              <i className="fa-brands fa-instagram text-xs" aria-hidden="true" />
            </a>

            {/* Direct Phone CTA Button */}
            <a
              href={`tel:${COMPANY_INFO.phoneRaw}`}
              className="hidden sm:inline-flex items-center justify-center gap-2 bg-[#E07A38] hover:bg-[#C96426] text-white font-bold uppercase tracking-wider text-xs px-4 py-2 rounded-md transition-all shadow-md"
            >
              <i className="fa-solid fa-phone text-white text-xs" aria-hidden="true" />
              <span>{COMPANY_INFO.phone}</span>
            </a>

            {/* Mobile Hamburger Button */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="min-[1100px]:hidden p-2 rounded-md text-stone-800 hover:text-black hover:bg-stone-100 focus:outline-none"
              aria-label="Åbn menu"
              aria-expanded={mobileMenuOpen}
            >
              <i className={`fa-solid ${mobileMenuOpen ? "fa-xmark" : "fa-bars"} text-xl text-[#E07A38]`} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* CONTACT BUTTONS STRIP RIGHT UNDER MENU HEADER */}
      {!mobileMenuOpen && (
        <div className="hidden sm:block bg-transparent text-white border-t border-zinc-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-0 pb-2.5">
            <div className="flex items-center justify-end">
              
              {/* 3 Main Action Buttons Group (Tilbud, Phone, Email) - Right Aligned with gap */}
              <div className="hidden sm:flex flex-wrap items-center justify-end gap-3 sm:gap-4 ml-auto -mt-px">
                
                {/* 1. TILBUD BUTTON */}
                <Link
                  to="/kontakt"
                  className="bg-[#E07A38] hover:bg-[#C96426] text-white font-extrabold text-xs uppercase tracking-wider px-5 py-2.5 rounded-b-md rounded-t-none transition-all flex items-center gap-2 shadow-sm shrink-0 group focus:outline-none"
                >
                  <i className="fa-solid fa-file-pen text-white text-sm group-hover:scale-110 transition-transform" aria-hidden="true" />
                  <span>Få et uforpligtende tilbud</span>
                </Link>

                {/* 2. PHONE BUTTON */}
                <a
                  href={`tel:${COMPANY_INFO.phoneRaw}`}
                  className="bg-[#272A2E] hover:bg-zinc-700 text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-b-md rounded-t-none transition-all flex items-center gap-2.5 border border-t-0 border-zinc-700 focus:outline-none group"
                >
                  <i className="fa-solid fa-phone text-[#E07A38] text-sm group-hover:scale-110 transition-transform" aria-hidden="true" />
                  <span>+{COMPANY_INFO.phone}</span>
                </a>

                {/* 3. EMAIL BUTTON */}
                <a
                  href={`mailto:${COMPANY_INFO.email}`}
                  className="bg-[#272A2E] hover:bg-zinc-700 text-stone-200 hover:text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-b-md rounded-t-none transition-all flex items-center gap-2.5 border border-t-0 border-zinc-700 focus:outline-none group"
                >
                  <i className="fa-solid fa-envelope text-[#E07A38] text-sm group-hover:scale-110 transition-transform" aria-hidden="true" />
                  <span className="truncate max-w-[200px] sm:max-w-none">{COMPANY_INFO.email}</span>
                </a>

              </div>

            </div>
          </div>
        </div>
      )}

      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && (
        <div className="min-[1100px]:hidden bg-slate-950 border-b border-slate-800 shadow-2xl animate-slideDown">
          <div className="px-4 py-4 space-y-2 max-h-[80vh] overflow-y-auto">
            {NAV_ITEMS.map((item) => {
              if (item.children) {
                return (
                  <div key={item.label} className="space-y-1">
                    <button
                      onClick={() => setServicesDropdownOpen(!servicesDropdownOpen)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded text-sm font-bold uppercase tracking-wider transition-colors ${
                        servicesDropdownOpen ? "bg-slate-900 text-[#d96b27]" : "text-slate-200 hover:bg-slate-900"
                      }`}
                    >
                      <span>{item.label}</span>
                      <i className={`fa-solid fa-chevron-down text-xs transition-transform ${servicesDropdownOpen ? "rotate-180 text-[#d96b27]" : ""}`} aria-hidden="true" />
                    </button>

                    {servicesDropdownOpen && (
                      <div className="pl-4 pr-2 py-1 space-y-1 bg-slate-900 rounded border-l-2 border-[#d96b27]">
                        {item.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            className={`flex items-center gap-2 px-3 py-2.5 rounded text-xs font-bold uppercase tracking-wider transition-colors ${
                              isActive(child.path)
                                ? "text-[#d96b27] bg-slate-800"
                                : "text-slate-300 hover:text-white hover:bg-slate-800"
                            }`}
                          >
                            <span>{child.label}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block px-4 py-3 rounded text-sm font-bold uppercase tracking-wider transition-colors ${
                    isActive(item.path)
                      ? "text-[#d96b27] bg-slate-900"
                      : "text-slate-200 hover:text-white hover:bg-slate-900"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}

            <div className="pt-4 mt-2 border-t border-slate-800 space-y-3">
              <a
                href={`tel:${COMPANY_INFO.phoneRaw}`}
                className="w-full flex items-center justify-center gap-2 bg-slate-800 text-white font-bold uppercase tracking-wider py-3 px-4 rounded-md text-center hover:bg-slate-700 transition-colors text-xs"
              >
                <i className="fa-solid fa-phone text-[#d96b27]" aria-hidden="true" />
                <span>Ring {COMPANY_INFO.phone}</span>
              </a>
              <Link
                to="/kontakt"
                className="w-full flex items-center justify-center gap-2 bg-[#d96b27] text-white font-bold uppercase tracking-wider py-3 px-4 rounded-md text-center hover:bg-[#c25a1f] transition-colors text-xs"
              >
                <i className="fa-solid fa-file-signature text-xs" aria-hidden="true" />
                <span>Indhent Uforpligtende Tilbud</span>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
